// Knowledge — the /bss explain Q&A store (plans/knowledge-explain-plan.md in
// boom-shipping-service). Lists cached answers with their verified date, ask
// count, and citations; a human can delete a wrong/obsolete entry or force a
// re-verify pass now (the weekly scheduler otherwise owns freshness).

import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import Shell from "./Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  RefreshButton,
  apiErrorMessage,
  relativeTime
} from "./shared";

const STATUS_COLOR = {
  answered: "var(--bss-success)",
  pending: "var(--bss-info)",
  stale: "var(--bss-warning)",
  failed: "var(--bss-danger)"
};

function CitationList({ citations }) {
  const list = Array.isArray(citations) ? citations : [];
  if (list.length === 0) {
    return (
      <span style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}>
        no citations — cannot auto-re-verify
      </span>
    );
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.6 }}>
      {list.map((c, i) => (
        <li key={i} style={{ color: "var(--bss-fg-muted)" }}>
          <code>
            {c.repoId}/{c.path}
            {c.line ? `:${c.line}` : ""}
          </code>
        </li>
      ))}
    </ul>
  );
}

function KnowledgeRow({ entry, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bss-card" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span
          className="bss-pill"
          style={{
            background: STATUS_COLOR[entry.status] || "var(--bss-fg-subtle)",
            color: "#fff",
            flexShrink: 0
          }}
        >
          {entry.status}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            onClick={() => setOpen((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textAlign: "left",
              fontSize: 14,
              fontWeight: 600,
              color: "var(--bss-fg-strong)"
            }}
          >
            {entry.question}
          </button>
          <div
            style={{
              fontSize: 12,
              color: "var(--bss-fg-subtle)",
              marginTop: 4
            }}
          >
            asked {entry.askCount}× · answered{" "}
            {entry.answeredAt ? relativeTime(entry.answeredAt) : "—"} · verified{" "}
            {entry.verifiedAt ? relativeTime(entry.verifiedAt) : "never"}
            {entry.costUsd != null
              ? ` · $${Number(entry.costUsd).toFixed(2)}`
              : ""}
          </div>
        </div>
        <button
          className="bss-btn bss-btn-danger"
          title="Delete this entry (a future ask re-analyzes fresh)"
          onClick={() => onDelete(entry.id)}
        >
          <Icon.X size={13} />
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          {entry.error && (
            <div
              style={{
                color: "var(--bss-danger)",
                fontSize: 13,
                marginBottom: 8
              }}
            >
              {entry.error}
            </div>
          )}
          {entry.answer ? (
            <pre
              style={{
                fontSize: 13,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                color: "var(--bss-fg-strong)",
                marginBottom: 10
              }}
            >
              {entry.answer}
            </pre>
          ) : (
            <div
              style={{
                fontSize: 13,
                color: "var(--bss-fg-subtle)",
                marginBottom: 10
              }}
            >
              (no answer yet)
            </div>
          )}
          <div
            style={{
              fontSize: 11.5,
              color: "var(--bss-fg-subtle)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 4
            }}
          >
            Cited files (drive auto-re-verification)
          </div>
          <CitationList citations={entry.citations} />
        </div>
      )}
    </div>
  );
}

export default function KnowledgePage() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [reverifying, setReverifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.bssKnowledge();
      setEntries(res.data.entries || []);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this knowledge entry? The next ask re-analyzes from scratch."
      )
    )
      return;
    try {
      await api.bssKnowledgeDelete(id);
      load();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const onReverify = async () => {
    setReverifying(true);
    try {
      await api.bssKnowledgeReverify();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setTimeout(() => setReverifying(false), 3000);
    }
  };

  return (
    <Shell>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>
          Knowledge — /bss explain answers
        </h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="bss-btn"
            onClick={onReverify}
            disabled={reverifying}
          >
            {reverifying ? "Re-verifying…" : "Re-verify all now"}
          </button>
          <RefreshButton onClick={load} />
        </div>
      </div>
      <KnowledgeBody entries={entries} error={error} onDelete={onDelete} />
    </Shell>
  );
}

function KnowledgeBody({ entries, error, onDelete }) {
  if (error) return <ErrorState message={error} />;
  if (entries == null) return <LoadingState />;
  if (entries.length === 0) {
    return (
      <EmptyState
        title="No knowledge yet"
        hint="Ask something in Slack: /bss explain how does X work?"
      />
    );
  }
  return entries.map((e) => (
    <KnowledgeRow key={e.id} entry={e} onDelete={onDelete} />
  ));
}
